import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { callFn, streamChat } from '../lib/api';
import type { ChatWireMessage } from '../lib/api';
import { parseUIActions } from '../lib/uiActions';
import type { UIAction } from '../lib/uiActions';
import { VoiceSession, voiceSupported } from '../lib/voice';
import type { VoiceState } from '../lib/voice';
import { useApp } from './AppContext';
import { useStore } from './StoreContext';

export interface ChatMsg { role: 'user' | 'assistant'; text: string; suggestions?: { item: string; price: string | null }[]; }

interface ChatState {
  /** Popup open/closed — the conversation lives here so it survives navigation. */
  open: boolean;
  setOpen: (v: boolean) => void;
  ready: boolean;          // system prompt loaded
  available: boolean;      // signed in + bound to this store
  messages: ChatMsg[];
  busy: boolean;
  error: string | null;
  send: (text: string) => Promise<void>;
  cancel: () => void;
  // voice
  voiceSupported: boolean;
  voiceState: VoiceState;
  transcript: string;
  startVoice: () => void;
  stopVoice: () => void;
}

const Ctx = createContext<ChatState | null>(null);
export function useChat(): ChatState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useChat must be used within ChatProvider');
  return v;
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const { slug, boundHere, addToCart, setCartQuantity, removeFromCart, clearCart } = useStore();
  const { authed, ensureSession, toast } = useApp();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [transcript, setTranscript] = useState('');
  const abort = useRef<AbortController | null>(null);
  // One chat thread per page load — lets the gateway keep a warm claude
  // session for this conversation (new load = new thread = new session).
  const conversationId = useRef<string>(
    crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
  );
  const voice = useRef<VoiceSession | null>(null);
  const msgsRef = useRef<ChatMsg[]>([]);
  msgsRef.current = messages;

  const available = authed && boundHere;

  // Opening the chat without a session starts a guest one (no account needed).
  useEffect(() => { if (open && !authed) void ensureSession(); }, [open, authed, ensureSession]);

  useEffect(() => {
    if (!available) { setSystemPrompt(null); return; }
    let live = true;
    callFn<{ systemPrompt: string }>('get_chat_context')
      .then((r) => { if (live) setSystemPrompt(r.systemPrompt); })
      .catch((e) => { if (live) setError(e instanceof Error ? e.message : 'Chat is unavailable.'); });
    return () => { live = false; };
  }, [available, slug]);

  // Leaving the store (slug change) ends any voice session.
  useEffect(() => () => { voice.current?.stop(); voice.current = null; }, [slug]);

  const apply = useCallback((actions: UIAction[]) => {
    let suggestions: ChatMsg['suggestions'];
    for (const a of actions) {
      switch (a.type) {
        case 'add_to_cart': addToCart(a.item, a.quantity, a.notes || undefined); toast(`${a.item} added to cart`, 'ok'); break;
        case 'remove_from_cart': removeFromCart(a.item); break;
        case 'clear_cart': clearCart(); toast('Cart cleared', 'ok'); break;
        case 'set_quantity': setCartQuantity(a.item, a.quantity); break;
        case 'show_cart': nav(`/${slug}/cart`); break;
        case 'show_category': nav(`/${slug}`, { state: { category: a.category } }); break;
        case 'show_item': nav(`/${slug}`, { state: { category: a.category, item: a.item } }); break;
        case 'show_hours': nav(`/${slug}/hours`); break;
        case 'show_about': nav(`/${slug}/about`); break;
        case 'suggest_items': suggestions = a.items; break;
      }
    }
    return suggestions;
  }, [addToCart, removeFromCart, setCartQuantity, clearCart, nav, slug, toast]);

  const cancel = useCallback(() => { abort.current?.abort(); }, []);

  const send = useCallback(async (text: string) => {
    const t = text.trim();
    if (!t || !systemPrompt) return;
    if (abort.current) abort.current.abort();
    setError(null);
    const history: ChatWireMessage[] = [
      { role: 'system', content: systemPrompt },
      ...msgsRef.current.slice(-12).map((m) => ({ role: m.role, content: m.text } as ChatWireMessage)),
      { role: 'user', content: t },
    ];
    setMessages((m) => [...m, { role: 'user', text: t }, { role: 'assistant', text: '' }]);
    setBusy(true);
    voice.current?.beginThinking(t);
    const ac = new AbortController(); abort.current = ac;
    let full = '';
    try {
      await streamChat(history, (chunk) => {
        full += chunk;
        const shown = full.replace(/```ui[\s\S]*$/, '').trimEnd();
        setMessages((m) => { const n = m.slice(); n[n.length - 1] = { role: 'assistant', text: shown }; return n; });
      }, ac.signal, conversationId.current);
      const { cleanedText, actions } = parseUIActions(full);
      const suggestions = apply(actions);
      const finalText = cleanedText || (actions.length ? 'Done.' : '');
      setMessages((m) => { const n = m.slice(); n[n.length - 1] = { role: 'assistant', text: finalText, suggestions }; return n; });
      voice.current?.speakReply(finalText);
    } catch (e) {
      setMessages((m) => (m[m.length - 1]?.role === 'assistant' && !m[m.length - 1]!.text ? m.slice(0, -1) : m));
      if (!ac.signal.aborted) setError(e instanceof Error ? e.message : 'Chat failed.');
      voice.current?.replyFailed();
    } finally {
      if (abort.current === ac) abort.current = null;
      setBusy(false);
    }
  }, [systemPrompt, apply]);

  const sendRef = useRef(send); sendRef.current = send;

  const startVoice = useCallback(() => {
    if (voice.current?.isActive) return;
    setOpen(true);
    const session = new VoiceSession({
      onState: setVoiceState,
      onTranscript: setTranscript,
      onSubmit: (text) => { void sendRef.current(text); },
      onCancel: () => { abort.current?.abort(); toast('Cancelled.', 'info'); },
      onError: (msg) => { toast(msg, 'err'); },
    });
    voice.current = session;
    session.start();
  }, [toast]);

  const stopVoice = useCallback(() => {
    voice.current?.stop();
    voice.current = null;
    setVoiceState('idle');
    setTranscript('');
  }, []);

  const value = useMemo<ChatState>(() => ({
    open, setOpen, ready: !!systemPrompt, available, messages, busy, error, send, cancel,
    voiceSupported: voiceSupported(), voiceState, transcript, startVoice, stopVoice,
  }), [open, systemPrompt, available, messages, busy, error, send, cancel, voiceState, transcript, startVoice, stopVoice]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
